import { useQuery } from "@tanstack/react-query";
import { get, patch, post } from "./divertidogsApiUtils";
import { normalizarFecha } from "../utils/dateUtils";
import { HorarioSpa } from "./agendamientosSpaServicios";

export interface AsistenciaDiariaSpa {
  id: string;
  fechaGeneracion: Date;
  asistencias: ItemListadoAsistenciaDiariaSpa[];
}

export interface ItemListadoAsistenciaDiariaSpa {
  id: string;
  idMascota: string;
  nombreMascota: string;
  raza: string;
  nombrePropietario: string;
  idReservaSpa: string;
  horario: HorarioSpa;
  asistio: boolean;
}

export const obtenerAsistenciaDiariaSpa =
  async (): Promise<AsistenciaDiariaSpa | null> => {
    const resp: AsistenciaDiariaSpa | undefined = await get<
      AsistenciaDiariaSpa | undefined
    >("listas_asistencia_spa/hoy");
    if (resp) {
      return resp;
    } else {
      return null;
    }
  };

export const QUERY_OBTENER_ASISTENCIA_DIARIA_SPA =
  "QUERY_OBTENER_ASISTENCIA_DIARIA_SPA";

export const useObtenerAsistenciaDiariaSpa = () => {
  return useQuery(
    [QUERY_OBTENER_ASISTENCIA_DIARIA_SPA],
    async () => await obtenerAsistenciaDiariaSpa()
  );
};

export const generarAsistenciaDiariaSpa = async () => {
  await post<void, void>("listas_asistencia_spa/hoy", undefined);
};

export const checkInSpa = async ({
  idListaAsistencia,
  asistencias,
}: {
  idListaAsistencia: string;
  asistencias: Map<string, boolean>;
}) => {
  await patch<Object, void>(
    "listas_asistencia_spa/" + idListaAsistencia,
    Object.fromEntries(asistencias)
  );
};

export interface IAsistenciaProyectadaSpa {
  idMascota: string;
  nombreMascota: string;
  nombrePropietario: string;
  idReservaSpa: string;
  horario: HorarioSpa;
}

export type AsistenciaProyectadaSpa = AsistenciaFuturaSpa | AsistenciaPasadaSpa;

export interface AsistenciaFuturaSpa extends IAsistenciaProyectadaSpa {}

export interface AsistenciaPasadaSpa extends IAsistenciaProyectadaSpa {
  asistio: boolean;
}

export type NivelListaAsistenciaProyectadaSpa = "BAJO" | "MEDIO" | "ALTO";

export interface IListaAsistenciaProyectadaSpa {
  fecha: string;
  conteoMascotas: number;
  tipo: "PASADA" | "FUTURA";
  nivel: NivelListaAsistenciaProyectadaSpa;
  asistencias: AsistenciaProyectadaSpa[];
}

export interface ListaAsistenciaFuturaSpa
  extends IListaAsistenciaProyectadaSpa {
  tipo: "FUTURA";
  asistencias: AsistenciaFuturaSpa[];
}

export interface ListaAsistenciaPasadaSpa
  extends IListaAsistenciaProyectadaSpa {
  tipo: "PASADA";
  idLista: string;
  asistencias: AsistenciaPasadaSpa[];
}

export type ListaAsistenciaProyectadaSpa =
  | ListaAsistenciaFuturaSpa
  | ListaAsistenciaPasadaSpa;

export function esAsistenciaPasada(
  object: AsistenciaProyectadaSpa
): object is AsistenciaPasadaSpa {
  return "asistio" in object;
}

export const obtenerListasAsistenciasProyectadasSpa = async (): Promise<
  Array<ListaAsistenciaProyectadaSpa>
> => {
  let listas =
    (await get<Array<ListaAsistenciaProyectadaSpa>>(
      "listas_asistencia_spa/asistencias_proyectadas"
    )) || [];

  for (let i = 0; i < listas.length; i++) {
    let lista = listas[i];
    listas[i] = lista;
  }

  return listas;
};

export const QUERY_OBTENER_LISTAS_ASISTENCIAS_PROYECTADAS_SPA =
  "QUERY_OBTENER_LISTAS_ASISTENCIAS_PROYECTADAS_SPA";

export const useObtenerListasAsistenciasProyectadasSpa = () => {
  return useQuery(
    [QUERY_OBTENER_LISTAS_ASISTENCIAS_PROYECTADAS_SPA],
    async () => await obtenerListasAsistenciasProyectadasSpa()
  );
};

export const obtenerListaAsistenciaSpa = async (
  id: string
): Promise<AsistenciaDiariaSpa | null> => {
  const resp: AsistenciaDiariaSpa | undefined = await get<
    AsistenciaDiariaSpa | undefined
  >(`listas_asistencia_spa/${id}`);
  if (resp) {
    return resp;
  } else {
    return null;
  }
};

export const QUERY_OBTENER_ASISTENCIA_SPA = "QUERY_OBTENER_ASISTENCIA_SPA";

export const useObtenerListaAsistenciaSpa = (id: string) => {
  return useQuery(
    [QUERY_OBTENER_ASISTENCIA_SPA],
    async () => await obtenerListaAsistenciaSpa(id)
  );
};

export const existeListaAsistenciaDeHoySpa = async () => {
  return get<boolean>(`listas_asistencia_spa/hoy/existe`);
};

export const QUERY_EXISTE_LISTA_ASISTENCIA_DE_HOY_SPA =
  "QUERY_EXISTE_LISTA_ASISTENCIA_DE_HOY_SPA";

export const useExisteListaAsistenciaDeHoySpa = () => {
  return useQuery(
    [QUERY_EXISTE_LISTA_ASISTENCIA_DE_HOY_SPA],
    async () => await existeListaAsistenciaDeHoySpa()
  );
};

export const QUERY_FECHAS_PROYECTADAS_SPA = "QUERY_FECHAS_PROYECTADAS_SPA";

export const useObtenerFechasProyectadasSpa = (idMascota: string) => {
  return useQuery(
    [QUERY_FECHAS_PROYECTADAS_SPA],
    async () => await obtenerFechasProyectadasSpa(idMascota)
  );
};

export const obtenerFechasProyectadasSpa = async (idMascota: string) => {
  const res = await get<Date[]>(
    `listas_asistencia_spa/fechas_proyectadas?idMascota=${idMascota}`
  );
  if (res === undefined) {
    throw new Error("No se pudo obtener las fechas proyectadas");
  }
  return res.map((fecha) => normalizarFecha(fecha));
};
